import { Controller } from '@hotwired/stimulus'

interface DropdownEvent extends CustomEvent {
  detail: {
    source: HTMLElement
  }
}

export class DropdownController extends Controller<HTMLDivElement> {
  declare trigger: HTMLButtonElement | null
  declare popover: HTMLElement | null
  declare menu?: HTMLElement | null

  menuItems: HTMLElement[] = []
  activeIndex: number = -1

  connect() {
    this.trigger = this.element.querySelector(':scope > button')
    this.popover = this.element.querySelector(':scope > [data-popover]')
    this.menu = this.popover?.querySelector('[role="menu"]')

    if (!this.trigger || !this.popover || !this.menu) {
      console.error('DropdownController: Missing required elements')
      return
    }

    this.trigger.addEventListener('click', this.onButtonClick)
    this.element.addEventListener('keydown', this.onMenuKeydown)
    this.menu.addEventListener('mousemove', this.onMenuMousemove)
    this.menu.addEventListener('mouseleave', this.onMouseLeave)
    this.menu.addEventListener('click', this.onMenuClick)
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('basecoat:popover', this.onPopoverEvent)
  }

  disconnect() {
    this.trigger?.removeEventListener('click', this.onButtonClick)
    this.element.removeEventListener('keydown', this.onMenuKeydown)
    this.menu?.removeEventListener('mousemove', this.onMenuMousemove)
    this.menu?.removeEventListener('mouseleave', this.onMouseLeave)
    this.menu?.removeEventListener('click', this.onMenuClick)
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('basecoat:popover', this.onPopoverEvent)
  }

  closePopover = (focusOnTrigger = true) => {
    if (this.trigger?.getAttribute('aria-expanded') === 'false') return
    this.trigger?.setAttribute('aria-expanded', 'false')
    this.trigger?.removeAttribute('aria-activedescendant')
    this.popover?.setAttribute('aria-hidden', 'true')

    if (focusOnTrigger) {
      this.trigger?.focus()
    }

    this.setActiveItem(-1)
  }

  openPopover = (initialSelection: boolean | 'first' | 'last' = false) => {
    if (!this.trigger || !this.popover || !this.menu) return

    document.dispatchEvent(
      new CustomEvent('basecoat:popover', {
        detail: { source: this.element },
      })
    )

    this.trigger.setAttribute('aria-expanded', 'true')
    this.popover.setAttribute('aria-hidden', 'false')
    this.menuItems = Array.from(
      this.menu.querySelectorAll<HTMLElement>('[role^="menuitem"]')
    ).filter(
      item =>
        !item.hasAttribute('disabled') &&
        item.getAttribute('aria-disabled') !== 'true'
    )

    if (this.menuItems.length > 0 && initialSelection) {
      if (initialSelection === 'first') {
        this.setActiveItem(0)
      } else if (initialSelection === 'last') {
        this.setActiveItem(this.menuItems.length - 1)
      }
    }
  }

  setActiveItem = (index: number) => {
    if (this.activeIndex > -1 && this.menuItems[this.activeIndex]) {
      this.menuItems[this.activeIndex].classList.remove('active')
    }
    this.activeIndex = index
    if (this.activeIndex > -1 && this.menuItems[this.activeIndex]) {
      const activeItem = this.menuItems[this.activeIndex]
      activeItem.classList.add('active')
      this.trigger?.setAttribute('aria-activedescendant', activeItem.id)
    } else {
      this.trigger?.removeAttribute('aria-activedescendant')
    }
  }

  private onButtonClick = () => {
    const isExpanded = this.trigger?.getAttribute('aria-expanded') === 'true'

    if (isExpanded) {
      this.closePopover()
    } else {
      this.openPopover(false)
    }
  }

  private onMenuKeydown = (event: KeyboardEvent) => {
    const isExpanded = this.trigger?.getAttribute('aria-expanded') === 'true'

    if (event.key === 'Escape') {
      if (isExpanded) this.closePopover()
      return
    }

    if (!isExpanded) {
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault()
        this.openPopover(false)
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.openPopover('first')
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.openPopover('last')
      }
      return
    }

    if (this.menuItems.length === 0) return

    let nextIndex = this.activeIndex

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        nextIndex =
          this.activeIndex === -1
            ? 0
            : Math.min(this.activeIndex + 1, this.menuItems.length - 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        nextIndex =
          this.activeIndex === -1
            ? this.menuItems.length - 1
            : Math.max(this.activeIndex - 1, 0)
        break
      case 'Home':
        event.preventDefault()
        nextIndex = 0
        break
      case 'End':
        event.preventDefault()
        nextIndex = this.menuItems.length - 1
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        this.menuItems[this.activeIndex]?.click()
        this.closePopover()
        return
    }

    if (nextIndex !== this.activeIndex) {
      this.setActiveItem(nextIndex)
    }
  }

  private onMenuMousemove = (event: MouseEvent) => {
    const menuItem = (event.target as HTMLElement).closest(
      '[role^="menuitem"]'
    ) as HTMLElement

    if (menuItem && this.menuItems.includes(menuItem)) {
      const index = this.menuItems.indexOf(menuItem)
      if (index !== this.activeIndex) {
        this.setActiveItem(index)
      }
    }
  }

  private onMouseLeave = () => {
    this.setActiveItem(-1)
  }

  private onMenuClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest('[role^="menuitem"]')) {
      this.closePopover()
    }
  }

  private onDocumentClick = (event: MouseEvent) => {
    if (!this.element.contains(event.target as Node)) {
      this.closePopover()
    }
  }

  private onPopoverEvent = (event: Event) => {
    const customEvent = event as DropdownEvent
    if (customEvent.detail.source !== this.element) {
      this.closePopover(false)
    }
  }
}
